import {
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  Dispatch,
  SetStateAction,
} from "react";
import { useForm } from "@mantine/form";
import {
  Combobox,
  Loader,
  Select,
  TextInput,
  useCombobox,
} from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo, faSearch } from "@fortawesome/sharp-regular-svg-icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePostHog, useFeatureFlagEnabled } from "posthog-js/react";
import { useMutation, useQuery } from "@apollo/client/react";
import PathConstants from "../../routes/path-constants";
import {
  Condition,
  Medication,
} from "shared/src/data/medication-condition-pairs";
import { conditionSearchTerms } from "shared/src/data/condition-search-terms";
import {
  createFuzzyFilter,
  createPairMatcher,
  MedicationConditionPairItem,
} from "../../utils/fuzzy-select-filter";
import { appendQueryParams } from "../../utils/url-utils";
import { isPansOrPandas } from "../../survey/utils";
import { Toast } from "design-system/src/components/toast";
import { gql } from "../../__generated__";
import { EligibilityScenario } from "../../__generated__/graphql";
import { ANONYMOUS_APPEAL_STORAGE_KEY } from "shared/src/constants/sign-out-storage-keys";
import { Button } from "../../components/product/buttons/button";
import CantFindModal from "./cant-find-modal/cant-find-modal";
import { useDelayedLoading } from "shared/src/hooks/use-delayed-loading";

import styles from "./medication-condition-eligibility-form.module.scss";

export const CREATE_APPEAL_STAGING_MUTATION = gql(`
  mutation CreateAppealStaging($input: CreateAppealStagingInput!) {
    createAppealStaging(input: $input) {
      appealId
    }
  }
`);

export const CONDITIONS_FOR_ELIGIBILITY_QUERY = gql(`
  query ConditionsForEligibility($coverageFundAppeal: Boolean!) {
    conditionsForEligibility(coverageFundAppeal: $coverageFundAppeal)
  }
`);

export const MEDICATIONS_FOR_CONDITION_QUERY = gql(`
  query MedicationsForCondition(
    $condition: String!
    $coverageFundAppeal: Boolean!
  ) {
    medicationsForCondition(
      condition: $condition
      coverageFundAppeal: $coverageFundAppeal
    )
  }
`);

export const MEDICATION_CONDITION_PAIRS_QUERY = gql(`
  query MedicationConditionPairsForEligibility($coverageFundAppeal: Boolean!) {
    medicationConditionPairsForEligibility(coverageFundAppeal: $coverageFundAppeal) {
      medication
      condition
      medicationAliases
      conditionAliases
    }
  }
`);

export interface MedicationConditionEligibilityFormRef {
  submit: () => Promise<boolean>;
}

export interface MedicationConditionEligibilityFormProps {
  setIsDisabled: Dispatch<SetStateAction<boolean>>;
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
  isDisabled: boolean;
}

type MedicationConditionFormData = {
  medication: string | null;
  condition: string | null;
};

type CombinedFormData = {
  selectedPair: string | null;
};

const SEPARATOR = "|||";

const getInitialCondition = (
  conditionInState: string | null,
  preSelectedCondition: string | null,
  conditions: Condition[],
) => {
  if (conditionInState) {
    return conditionInState;
  }

  if (preSelectedCondition) {
    const conditionMatchingPreSelection = conditions.find(
      (condition) =>
        condition.toLowerCase() === preSelectedCondition.toLowerCase(),
    );

    return conditionMatchingPreSelection ?? "";
  }

  return "";
};

const getInitialMedication = (
  medicationInState: string | null,
  preSelectedMedication: string | null,
  availableMedications: string[],
) => {
  if (medicationInState) {
    const isValid = availableMedications.includes(medicationInState);
    return isValid ? medicationInState : "";
  }

  if (preSelectedMedication) {
    const medicationMatchingPreSelection = availableMedications.find(
      (medication) =>
        medication.toLowerCase() === preSelectedMedication.toLowerCase(),
    );

    return medicationMatchingPreSelection ?? "";
  }

  return "";
};

const LegacyEligibilityForm = forwardRef<
  MedicationConditionEligibilityFormRef,
  MedicationConditionEligibilityFormProps
>(function LegacyEligibilityForm(
  { setIsDisabled, onSubmit, isSubmitting, isDisabled },
  ref,
) {
  const [searchParams] = useSearchParams();
  const effectiveEligibility = {
    condition: null,
    medication: null,
    coverageFundAppeal: searchParams.get("coverageFund") === "true",
  };
  const posthog = usePostHog();
  const navigate = useNavigate();
  const [createAppealStaging, { error: stagingError }] = useMutation(
    CREATE_APPEAL_STAGING_MUTATION,
  );

  const {
    data: conditionsData,
    loading: conditionsLoading,
    error: conditionsError,
  } = useQuery(CONDITIONS_FOR_ELIGIBILITY_QUERY, {
    variables: {
      coverageFundAppeal: effectiveEligibility.coverageFundAppeal,
    },
    fetchPolicy: "no-cache",
  });
  const conditions = useMemo(
    () => (conditionsData?.conditionsForEligibility ?? []) as Condition[],
    [conditionsData],
  );

  const conditionSelectData = useMemo(
    () =>
      conditions.map((condition) =>
        condition === Condition.MASH
          ? {
              value: condition,
              label: "MASH (Metabolic dysfunction–associated steatohepatitis)",
            }
          : condition,
      ),
    [conditions],
  );

  const conditionFilter = useMemo(
    () => createFuzzyFilter(conditionSearchTerms),
    [],
  );

  const preSelectedConditionParam = searchParams.get("condition");
  const preSelectedMedicationParam = searchParams.get("medication");

  const form = useForm<MedicationConditionFormData>({
    initialValues: {
      medication: effectiveEligibility.medication || "",
      condition: "",
    },
    validate: {
      medication: (value) => (value ? null : "Medication denied is required"),
      condition: (value) => (value ? null : "Health condition is required"),
    },
    validateInputOnBlur: true,
    validateInputOnChange: true,
  });

  const {
    data: medicationsData,
    loading: medicationsLoading,
    error: medicationsError,
  } = useQuery(MEDICATIONS_FOR_CONDITION_QUERY, {
    variables: {
      condition: form.values.condition ?? "",
      coverageFundAppeal: effectiveEligibility.coverageFundAppeal,
    },
    fetchPolicy: "no-cache",
    skip: !form.values.condition,
  });
  const medications = medicationsLoading
    ? []
    : (medicationsData?.medicationsForCondition ?? []);

  const showConditionsLoading = useDelayedLoading(conditionsLoading);
  const showMedicationsLoading = useDelayedLoading(medicationsLoading);

  useEffect(() => {
    form.setFieldValue(
      "condition",
      getInitialCondition(
        effectiveEligibility.condition,
        preSelectedConditionParam,
        conditions,
      ),
    );
    // leave out setFieldValue as it causes re-render and locks up the page
  }, [preSelectedConditionParam, conditions, effectiveEligibility.condition]);

  useEffect(() => {
    if (form.values.condition && medications.length > 0) {
      form.setFieldValue(
        "medication",
        getInitialMedication(
          effectiveEligibility.medication,
          preSelectedMedicationParam,
          medications,
        ),
      );
    }
    // leave out setFieldValue as it causes re-render and locks up the page
  }, [
    preSelectedMedicationParam,
    medicationsData?.medicationsForCondition,
    effectiveEligibility.medication,
  ]);

  useEffect(() => {
    setIsDisabled(!form.values.medication || !form.values.condition);
  }, [form.values.medication, form.values.condition, setIsDisabled]);

  const createStagingAndNavigate = async (input: {
    medication: string;
    condition: string;
    eligibilityScenario?: EligibilityScenario;
  }) => {
    const resp = await createAppealStaging({
      variables: { input },
    });

    if (resp.data?.createAppealStaging?.appealId) {
      const appealId = resp.data.createAppealStaging.appealId;
      sessionStorage.setItem(ANONYMOUS_APPEAL_STORAGE_KEY, appealId);
      navigate(PathConstants.MyAppealsDetail.replace(":appealId", appealId));
    }
  };

  const handleFormSubmit = async ({
    medication,
    condition,
  }: MedicationConditionFormData): Promise<boolean> => {
    if (!medication || !condition) {
      return false;
    }

    if (medication === Medication.SUCRAID && condition === Condition.CSID) {
      if (searchParams.get("e2e-test") !== "true") {
        window.location.assign(PathConstants.SucraidRedirect);
        return false;
      }
    }

    if (
      medication === Medication.STRENSIQ &&
      condition === Condition.HYPOPHOSPHATASIA
    ) {
      if (searchParams.get("e2e-test") !== "true") {
        window.location.assign(PathConstants.StrensiqRedirect);
        return false;
      }
    }

    const eligibilityScenario = effectiveEligibility.coverageFundAppeal
      ? EligibilityScenario.CoverageFund
      : isPansOrPandas(condition)
        ? EligibilityScenario.PansPandasFormulary
        : undefined;

    await createStagingAndNavigate({
      medication,
      condition,
      eligibilityScenario,
    });
    return true;
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      const validationResult = form.validate();
      if (validationResult.hasErrors) {
        return false;
      }
      return await handleFormSubmit(form.getValues());
    },
  }));

  return (
    <>
      <h1 className={styles.header}>
        {"Let's see if we can help. What were you denied coverage for?"}
      </h1>
      <div className={styles.eligibilityForm}>
        <Select
          label="Health condition being treated"
          data={conditionSelectData}
          key={form.key("condition")}
          searchable
          filter={conditionFilter}
          allowDeselect={false}
          rightSection={
            showConditionsLoading ? (
              <Loader size="xs" data-testid="condition-loading" />
            ) : (
              <FontAwesomeIcon icon={faSearch} title="search" />
            )
          }
          classNames={{
            option: styles.formSelectOption,
          }}
          data-testid="condition-select"
          {...form.getInputProps("condition")}
          onChange={(newCondition) => {
            form.setFieldValue("medication", "");

            form.getInputProps("condition").onChange(newCondition);
            form.clearErrors();

            if (newCondition) {
              posthog.capture("eligibility_condition_selected", {
                condition: newCondition,
              });
              posthog.setPersonProperties({
                condition: newCondition,
              });
            }
          }}
        />
        {form.values.condition && (
          <Select
            label="Medication denied"
            data={["", ...medications]}
            key={form.key("medication")}
            searchable
            allowDeselect={false}
            rightSection={
              showMedicationsLoading ? (
                <Loader size="xs" data-testid="medication-loading" />
              ) : (
                <FontAwesomeIcon icon={faSearch} title="search" />
              )
            }
            classNames={{
              option: styles.formSelectOption,
            }}
            data-testid="medication-select"
            {...form.getInputProps("medication")}
            onChange={(newMedication) => {
              form.getInputProps("medication").onChange(newMedication);

              if (newMedication) {
                posthog.capture("eligibility_medication_selected", {
                  medication: newMedication,
                });
                posthog.setPersonProperties({
                  medication: newMedication,
                });
              }
            }}
          />
        )}
      </div>
      <Toast
        variant="error"
        isVisible={!!stagingError || !!conditionsError || !!medicationsError}
      >
        {stagingError
          ? "Something went wrong. Please try again."
          : "We couldn't load the available conditions or medications. Try again or contact support."}
      </Toast>
      <div className={styles.formActions}>
        <Button
          variant="primary"
          size="medium"
          type="button"
          isFullWidth
          loading={isSubmitting}
          disabled={isDisabled}
          onClick={onSubmit}
          data-testid="get-started-button"
        >
          Get Started
        </Button>
      </div>
      <div className={styles.cantFindDeniedCareLink}>
        <a
          href={appendQueryParams(PathConstants.NotifyMe)}
          className={styles.cantFindDeniedCareLinkText}
          data-testid="cant-find-denied-care-link"
          onClick={() => {
            posthog.capture(
              "eligibility_notify_me_clicked",
              {
                source: "cant_find_denied_care",
                condition: form.values.condition || null,
                medication: form.values.medication || null,
              },
              { send_instantly: true },
            );
          }}
        >
          <FontAwesomeIcon icon={faCircleInfo} className={styles.linkIcon} />
          {"I can't find my denied care"}
        </a>
      </div>
    </>
  );
});

const CombinedEligibilityForm = forwardRef<
  MedicationConditionEligibilityFormRef,
  MedicationConditionEligibilityFormProps
>(function CombinedEligibilityForm(
  { setIsDisabled, onSubmit, isSubmitting, isDisabled },
  ref,
) {
  const [searchParams] = useSearchParams();
  const coverageFundAppeal = searchParams.get("coverageFund") === "true";
  const posthog = usePostHog();
  const navigate = useNavigate();
  const searchTextRef = useRef<string>("");
  const [isCantFindModalOpen, setIsCantFindModalOpen] = useState(false);

  const [createAppealStaging, { error: stagingError }] = useMutation(
    CREATE_APPEAL_STAGING_MUTATION,
  );

  const {
    data: pairsData,
    loading: pairsLoading,
    error: pairsError,
  } = useQuery(MEDICATION_CONDITION_PAIRS_QUERY, {
    variables: { coverageFundAppeal },
    fetchPolicy: "cache-first",
  });

  const preSelectedConditionParam = searchParams.get("condition");
  const preSelectedMedicationParam = searchParams.get("medication");

  const pairSearchItems: MedicationConditionPairItem[] = useMemo(() => {
    if (!pairsData) return [];
    return pairsData.medicationConditionPairsForEligibility.map((pair) => ({
      value: `${pair.medication}${SEPARATOR}${pair.condition}`,
      medicationName: pair.medication,
      conditionName: pair.condition,
      medicationAliases: [...pair.medicationAliases],
      conditionAliases: [...pair.conditionAliases],
    }));
  }, [pairsData]);

  const pairLabels = useMemo(() => {
    const map = new Map<string, string>();
    pairSearchItems.forEach((pair) => {
      map.set(pair.value, `${pair.medicationName} for ${pair.conditionName}`);
    });
    return map;
  }, [pairSearchItems]);

  const pairMatcher = useMemo(() => {
    if (pairSearchItems.length === 0) return null;
    return createPairMatcher(pairSearchItems);
  }, [pairSearchItems]);

  const [searchText, setSearchText] = useState("");

  const filteredPairs = useMemo(() => {
    if (!pairMatcher) return [];
    return pairMatcher(searchText);
  }, [pairMatcher, searchText]);

  const groupedFilteredPairs = useMemo(() => {
    const groups = new Map<string, MedicationConditionPairItem[]>();
    filteredPairs.forEach((pair) => {
      const existing = groups.get(pair.conditionName);
      if (existing) {
        existing.push(pair);
      } else {
        groups.set(pair.conditionName, [pair]);
      }
    });
    return Array.from(groups.entries()).map(([condition, items]) => ({
      condition,
      items,
    }));
  }, [filteredPairs]);

  useEffect(() => {
    if (filteredPairs.length > 0 || searchText.length < 3) {
      return;
    }
    const timer = setTimeout(() => {
      posthog.capture("eligibility_search_no_results", {
        searchText,
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [filteredPairs.length, searchText, posthog]);

  const form = useForm<CombinedFormData>({
    initialValues: {
      selectedPair: null,
    },
    validate: {
      selectedPair: (value) => (value ? null : "Select your denied care"),
    },
    validateInputOnBlur: true,
    validateInputOnChange: true,
  });

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex("active"),
  });

  useEffect(() => {
    searchTextRef.current = searchText;
  }, [searchText]);

  const handleOptionSubmit = (value: string) => {
    form.setFieldValue("selectedPair", value);
    setSearchText(pairLabels.get(value) ?? "");
    combobox.closeDropdown();

    const [medication, condition] = value.split(SEPARATOR);
    posthog.capture("eligibility_pair_selected", {
      medication,
      condition,
      searchText: searchTextRef.current,
    });
    posthog.setPersonProperties({ condition, medication });
  };

  const openCantFindModal = (source?: "no_results_dropdown") => {
    const [medication, condition] =
      form.values.selectedPair?.split(SEPARATOR) ?? [];
    posthog.capture("eligibility_cant_find_clicked", {
      searchText: searchTextRef.current,
      selectedMedication: medication || null,
      selectedCondition: condition || null,
      coverageFundAppeal,
      ...(source ? { source } : {}),
    });
    setIsCantFindModalOpen(true);
  };

  useEffect(() => {
    if (
      preSelectedConditionParam &&
      preSelectedMedicationParam &&
      pairSearchItems.length > 0
    ) {
      const match = pairSearchItems.find(
        (pair) =>
          pair.medicationName.toLowerCase() ===
            preSelectedMedicationParam.toLowerCase() &&
          pair.conditionName.toLowerCase() ===
            preSelectedConditionParam.toLowerCase(),
      );
      if (match) {
        form.setFieldValue("selectedPair", match.value);
        setSearchText(pairLabels.get(match.value) ?? "");
      }
    }
  }, [
    pairSearchItems,
    pairLabels,
    preSelectedConditionParam,
    preSelectedMedicationParam,
  ]);

  useEffect(() => {
    setIsDisabled(!form.values.selectedPair);
  }, [form.values.selectedPair, setIsDisabled]);

  const createStagingAndNavigate = async (input: {
    medication: string;
    condition: string;
    eligibilityScenario?: EligibilityScenario;
  }) => {
    const resp = await createAppealStaging({
      variables: { input },
    });

    if (resp.data?.createAppealStaging?.appealId) {
      const appealId = resp.data.createAppealStaging.appealId;
      sessionStorage.setItem(ANONYMOUS_APPEAL_STORAGE_KEY, appealId);
      navigate(PathConstants.MyAppealsDetail.replace(":appealId", appealId));
    }
  };

  const handleFormSubmit = async ({
    selectedPair,
  }: CombinedFormData): Promise<boolean> => {
    if (!selectedPair) return false;

    const [medication, condition] = selectedPair.split(SEPARATOR);
    if (!medication || !condition) return false;

    if (medication === Medication.SUCRAID && condition === Condition.CSID) {
      if (searchParams.get("e2e-test") !== "true") {
        window.location.assign(PathConstants.SucraidRedirect);
        return false;
      }
    }

    if (
      medication === Medication.STRENSIQ &&
      condition === Condition.HYPOPHOSPHATASIA
    ) {
      if (searchParams.get("e2e-test") !== "true") {
        window.location.assign(PathConstants.StrensiqRedirect);
        return false;
      }
    }

    const eligibilityScenario = coverageFundAppeal
      ? EligibilityScenario.CoverageFund
      : isPansOrPandas(condition)
        ? EligibilityScenario.PansPandasFormulary
        : undefined;

    await createStagingAndNavigate({
      medication,
      condition,
      eligibilityScenario,
    });
    return true;
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      const validationResult = form.validate();
      if (validationResult.hasErrors) {
        return false;
      }
      return await handleFormSubmit(form.getValues());
    },
  }));

  const isListOpen = combobox.dropdownOpened;
  const showEmptyState = isListOpen && filteredPairs.length === 0;

  return (
    <>
      <h1 className={styles.header}>
        {"Let's see if we can help. What were you denied coverage for?"}
      </h1>
      <div className={styles.eligibilityForm}>
        <Combobox
          store={combobox}
          onOptionSubmit={handleOptionSubmit}
          withinPortal={false}
        >
          <Combobox.EventsTarget withExpandedAttribute>
            <TextInput
              label="What were you denied coverage for?"
              placeholder="Search by medication or condition..."
              value={searchText}
              data-testid="combined-eligibility-search-input"
              rightSection={
                pairsLoading ? (
                  <Loader size="xs" data-testid="pairs-loading" />
                ) : (
                  <FontAwesomeIcon icon={faSearch} title="search" />
                )
              }
              onChange={(event) => {
                const next = event.currentTarget.value;
                setSearchText(next);
                if (form.values.selectedPair) {
                  form.setFieldValue("selectedPair", null);
                }
                combobox.openDropdown();
                combobox.updateSelectedOptionIndex();
              }}
              onClick={() => combobox.openDropdown()}
              onFocus={() => combobox.openDropdown()}
              onBlur={() => combobox.closeDropdown()}
            />
          </Combobox.EventsTarget>
          {isListOpen && (
            <div className={styles.inlineResults}>
              <Combobox.Options>
                {groupedFilteredPairs.map((group) => (
                  <Combobox.Group key={group.condition} label={group.condition}>
                    {group.items.map((item) => (
                      <Combobox.Option key={item.value} value={item.value}>
                        {pairLabels.get(item.value)}
                      </Combobox.Option>
                    ))}
                  </Combobox.Group>
                ))}
              </Combobox.Options>
              {showEmptyState && (
                <div className={styles.inlineResultsEmpty}>
                  <button
                    type="button"
                    className={styles.cantFindDropdownLink}
                    data-testid="cant-find-denied-care-dropdown-link"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openCantFindModal("no_results_dropdown");
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faCircleInfo}
                      className={styles.linkIcon}
                    />
                    {"I can't find my denied care"}
                  </button>
                </div>
              )}
            </div>
          )}
        </Combobox>
      </div>
      <Toast variant="error" isVisible={!!stagingError || !!pairsError}>
        {stagingError
          ? "Something went wrong. Please try again."
          : "We couldn't load the available medications. Try again or contact support."}
      </Toast>
      <div className={styles.formActions}>
        <Button
          variant="primary"
          size="medium"
          type="button"
          isFullWidth
          loading={isSubmitting}
          disabled={isDisabled}
          onClick={onSubmit}
          data-testid="get-started-button"
        >
          Get Started
        </Button>
        <div className={styles.cantFindRow}>
          <Button
            variant="secondary"
            size="small"
            type="button"
            data-testid="cant-find-denied-care-link"
            onClick={() => openCantFindModal()}
          >
            {"I can't find my denied care"}
          </Button>
        </div>
      </div>
      <CantFindModal
        isOpen={isCantFindModalOpen}
        onClose={() => setIsCantFindModalOpen(false)}
        searchText={searchTextRef.current}
        posthog={posthog}
      />
    </>
  );
});

const MedicationConditionEligibilityForm = forwardRef<
  MedicationConditionEligibilityFormRef,
  MedicationConditionEligibilityFormProps
>(function MedicationConditionEligibilityForm(props, ref) {
  const [searchParams] = useSearchParams();
  const combinedSearchParam = searchParams.get("combinedSearch");
  const featureFlag =
    useFeatureFlagEnabled("combined-eligibility-search") === true;
  const isCombinedSearch =
    combinedSearchParam !== null ? combinedSearchParam === "true" : featureFlag;

  if (isCombinedSearch) {
    return <CombinedEligibilityForm {...props} ref={ref} />;
  }

  return <LegacyEligibilityForm {...props} ref={ref} />;
});

export default MedicationConditionEligibilityForm;
